import { appContext } from '../../../../AppContext';
import { MediaSelector } from "@iconics/components";
import { IOntologyData } from './DataModels';
import { IbssComponent } from '../../../../Components/Core/BaseComponent/IbssComponent';
import { JSX } from 'react';
import { IState as IParentState, IFile } from './EditSolution';
import { MultilingualTextField } from "@iconics/components";
import { ILanguageValues } from "@iconics/localisation";
import { SerendieSymbol } from '@serendie/symbols';
import { Switch } from '@serendie/ui';
import { Card, GridContainer, GridItem, OnSectionUpdateHandler, Select, TextField, Typography } from '@iconics/components';

class EditSolutionDetailsForm extends IbssComponent<IProps, IState>
{

    private get labels() { return appContext().labels; }

    constructor(props: IProps)
    {
        super(props);
        this.state =
        {
        };
    }

    public render(): JSX.Element
    {
        return (
            <Card
                title={this.labels.Platform_Solution_DetailsTitle}
                subTitle={this.labels.Platform_Solution_DetailsSubTitle}
                onSectionUpdate={i => this.props.onSectionUpdate(i)}
                icon={<SerendieSymbol name='apps-square' />}
            >
                <GridContainer>
                    <GridItem expanded={6}>
                        <TextField
                            label={this.labels.Solution_Name}
                            placeholder={this.labels.Solution_Name}
                            value={this.props.name}
                            onChange={(e) => this.props.handleInputChange('name', e.target.value)}
                            required
                            invalid={!!this.props.nameError}
                            invalidMessage={this.props.nameError}
                        />
                    </GridItem>
                    <GridItem expanded={6}>
                        <MultilingualTextField
                            label={this.labels.Solution_DisplayName}
                            placeholder={this.labels.Solution_DisplayName}
                            value={this.props.displayName}
                            onChange={(value) => this.props.handleInputChange('displayName', value)}
                            localisation={appContext().localisation}
                        />
                    </GridItem>
                    <GridItem>
                        <TextField
                            label={this.labels.Solution_Description}
                            placeholder={this.labels.Solution_Description}
                            value={this.props.description}
                            onChange={(e) => this.props.handleInputChange('description', e.target.value)}
                            required
                            invalid={!!this.props.descriptionError}
                            invalidMessage={this.props.descriptionError}
                        />
                    </GridItem>
                    <GridItem>
                        {
                            this.props.isNew &&
                            <Select
                                label={this.labels.Solution_Ontology}
                                placeholder={this.props.ontologyName}
                                onSelect={(e) => this.props.handleInputChange('ontologyId', e.value)}
                                items={this.props.ontologies.filter(x => x.IsEnabled === 'Yes').map(x => ({ label: x.Name, value: x.Id }))}
                                required
                                invalid={!!this.props.ontologyError}
                                invalidMessage={this.props.ontologyError}
                            />
                        }
                        {
                            !this.props.isNew &&
                            <Select
                                label={this.labels.Solution_Ontology}
                                placeholder={this.props.ontologies.filter(x => x.Id === this.props.ontologyId)[0]?.Name ?? ''}
                                items={this.props.ontologies.filter(x => x.IsEnabled === 'Yes').map(x => ({ label: x.Name, value: x.Id }))}
                                disabled
                            />
                        }
                    </GridItem>
                    <GridItem>
                        <Switch
                            label={this.labels.Solution_IsEnabled}
                            checked={this.props.enabled}
                            onCheckedChange={(e) => this.props.handleInputChange('enabled', e.checked as boolean)}
                        />
                    </GridItem>
                    <GridItem>
                        <Switch
                            label={this.labels.Solution_ShowOnHomePage}
                            checked={this.props.showOnHomePage}
                            onCheckedChange={(e) => this.props.handleInputChange('showOnHomePage', e.checked as boolean)}
                        />
                    </GridItem>
                    <GridItem>
                        <Switch
                            label={this.labels.Solution_ShowOnMenu}
                            checked={this.props.showOnMenu}
                            onCheckedChange={(e) => this.props.handleInputChange('showOnMenu', e.checked as boolean)}
                        />
                    </GridItem>
                    <GridItem expanded={6}>
                        <TextField
                            label={this.labels.Solution_ApplicationUri}
                            placeholder={this.labels.Solution_ApplicationUri}
                            value={this.props.applicationLink}
                            onChange={(e) => this.props.handleInputChange('applicationLink', e.target.value)}
                        />
                    </GridItem>
                    <GridItem expanded={6}>
                        <TextField
                            label={this.labels.Solution_OrderSequence}
                            placeholder={this.labels.Solution_OrderSequence}
                            type='number'
                            value={this.props.order}
                            onChange={(e) => this.props.handleInputChange('order', e.target.value)}
                        />
                    </GridItem>
                    <GridItem expanded={6}>
                        <Typography role="label" size="large">{this.labels.Solution_ImageUri}</Typography>
                        <MediaSelector
                            options={this.props.images.map(i => ({ name: i.name, url: i.path }))}
                            imageSelected={(e) => this.props.handleInputChange('imageUri', e.url)}
                            onFileUploaded={() => this.props.loadImages()}
                            defaultSelectedImage={this.props.imageUri}
                            uploadPath='c/Solutions/'
                            allowedFileTypes={{
                                'image/jpeg':['jpeg', 'jpg'],
                                'image/png':['png'],
                                'image/svg+xml':['svg'],
                                'image/gif':['gif'],
                            }}
                        />
                    </GridItem>
                    <GridItem expanded={6}>
                        <Typography role="label" size="large">{this.labels.Solution_IconUri}</Typography>
                        <MediaSelector
                            options={this.props.images.map(i => ({ name: i.name, url: i.path }))}
                            imageSelected={(e) => this.props.handleInputChange('iconUri', e.url)}
                            onFileUploaded={() => this.props.loadImages()}
                            defaultSelectedImage={this.props.iconUri}
                            uploadPath='c/Solutions/'
                            allowedFileTypes={{
                                'image/jpeg':['jpeg', 'jpg'],
                                'image/png':['png'],
                                'image/svg+xml':['svg'],
                                'image/gif':['gif'],
                            }}
                        />
                    </GridItem>
                </GridContainer>
            </Card>
        )
    }
}

export default EditSolutionDetailsForm;

export interface IProps
{
    id: string;
    ontologyName: string;
    ontologyId: string;
    name: string;
    nameError: string;
    displayName: ILanguageValues;
    description: string;
    descriptionError: string;
    ontologyError: string;
    enabled: boolean;
    showOnHomePage: boolean;
    showOnMenu: boolean;
    images: IFile[];
    imageUri: string;
    iconUri: string;
    applicationLink: string;
    order: string;
    ontologies: IOntologyData[];
    isNew: boolean;
    handleInputChange: (fieldName: keyof IParentState, value: IParentState[keyof IParentState]) => void
    loadImages: () => Promise<void>;
    onSectionUpdate: OnSectionUpdateHandler;
}

export interface IState
{
}